import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { fadeUp, revealProps, stagger, useReducedMotion } from "homepage-new/motion";
import { rf } from "./theme";

// The handful of shapes the whole page is built from. Every card is outlined
// and offset the same way, so the page reads as one object rather than a stack
// of components that happen to share a colour.

export const Card = ({ children, sx, bg = rf.paper, shadow = rf.shadow, ...rest }) => (
  <Box
    sx={{
      backgroundColor: bg,
      border: rf.line,
      borderRadius: rf.radius,
      boxShadow: shadow,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

export const Pill = ({ children, bg = rf.paper, sx }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.5,
      py: 0.6,
      backgroundColor: bg,
      border: rf.lineThin,
      borderRadius: rf.radiusPill,
      fontFamily: rf.display,
      fontSize: 12.5,
      fontWeight: 700,
      color: rf.ink,
      lineHeight: 1.2,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const VARIANT = {
  primary: { backgroundColor: rf.coral, color: rf.ink },
  dark: { backgroundColor: rf.ink, color: "#fff" },
  light: { backgroundColor: rf.paper, color: rf.ink },
  amber: { backgroundColor: rf.amber, color: rf.ink },
};

// `pulse` makes a button ask for attention: it lifts out of its own shadow and
// settles back, on a loop. Deliberately not an opacity blink — a button that
// vanishes twice a second reads as broken, and anyone with vestibular or
// attention sensitivities pays for it. Stops dead under reduced motion.
const PULSE = {
  "@keyframes rfPulse": {
    "0%, 100%": { transform: "translate(0, 0)", boxShadow: rf.shadowSm },
    "50%": { transform: "translate(-2px, -2px)", boxShadow: "6px 6px 0 #141414" },
  },
  animation: "rfPulse 1.5s ease-in-out infinite",
};

// A button that physically presses: the shadow shrinks and the button moves
// into it. Disabled when reduced motion is on, via CSS only.
export const PushButton = ({ children, variant = "primary", size = "md", onClick, type, disabled, fullWidth, pulse, sx }) => {
  const scale = size === "lg" ? { px: 3.5, py: 1.5, fontSize: 17 } : size === "sm" ? { px: 2, py: 0.9, fontSize: 14 } : { px: 2.75, py: 1.2, fontSize: 15.5 };
  const pulsing = pulse && !disabled;

  return (
    <Box
      component="button"
      type={type || "button"}
      onClick={onClick}
      disabled={disabled}
      sx={{
        ...VARIANT[variant],
        ...scale,
        width: fullWidth ? "100%" : undefined,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        border: rf.line,
        borderRadius: rf.radiusPill,
        boxShadow: rf.shadowSm,
        fontFamily: rf.display,
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "transform 0.1s ease, box-shadow 0.1s ease",
        ...(pulsing ? PULSE : null),
        // Hovering takes over from the loop, so the press still feels direct.
        "&:hover": disabled ? {} : { animation: "none", transform: "translate(1px, 1px)", boxShadow: "2px 2px 0 #141414" },
        "&:active": disabled ? {} : { animation: "none", transform: "translate(3px, 3px)", boxShadow: "0 0 0 #141414" },
        "@media (prefers-reduced-motion: reduce)": { transition: "none", animation: "none" },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export const Display = ({ children, component = "h2", size = 44, sx }) => (
  <Typography
    component={component}
    sx={{
      fontFamily: rf.display,
      fontWeight: 800,
      letterSpacing: "-0.035em",
      lineHeight: 1.03,
      color: rf.ink,
      fontSize: { xs: Math.round(size * 0.64), sm: Math.round(size * 0.82), md: size },
      ...sx,
    }}
  >
    {children}
  </Typography>
);

export const Eyebrow = ({ children, bg = rf.amber }) => (
  <Pill bg={bg} sx={{ textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 11.5 }}>
    {children}
  </Pill>
);

// One band of the page. `band` paints the deeper cream; `boxed` puts the whole
// band inside an outlined card, which is how the reference site separates its
// big moments.
export const Band = ({ id, eyebrow, title, lead, children, band, boxed, bg, align = "center", maxWidth = "lg" }) => {
  const reduced = useReducedMotion();
  const headingId = title ? `${id}-heading` : undefined;

  const header = title && (
    <motion.div variants={stagger(0.07)} {...revealProps(reduced)} style={{ textAlign: align, marginBottom: 48 }}>
      {eyebrow && (
        <motion.div variants={fadeUp} style={{ marginBottom: 18 }}>
          <Eyebrow>{eyebrow}</Eyebrow>
        </motion.div>
      )}
      <motion.div variants={fadeUp}>
        <Display component="h2" id={headingId} size={46} sx={{ mb: lead ? 2 : 0, maxWidth: align === "center" ? 820 : 720, mx: align === "center" ? "auto" : 0 }}>
          {title}
        </Display>
      </motion.div>
      {lead && (
        <motion.div variants={fadeUp}>
          <Typography
            sx={{
              fontFamily: rf.body,
              fontSize: { xs: 16, md: 17.5 },
              lineHeight: 1.6,
              color: rf.inkSoft,
              maxWidth: 680,
              mx: align === "center" ? "auto" : 0,
            }}
          >
            {lead}
          </Typography>
        </motion.div>
      )}
    </motion.div>
  );

  const inner = (
    <>
      {header}
      {children}
    </>
  );

  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={headingId}
      sx={{ py: { xs: 7, md: 11 }, backgroundColor: band ? rf.creamDeep : "transparent" }}
    >
      <Container maxWidth={maxWidth}>
        {boxed ? (
          <Card bg={bg || rf.paper} shadow={rf.shadowLg} sx={{ p: { xs: 3, sm: 5, md: 7 } }}>
            {inner}
          </Card>
        ) : (
          inner
        )}
      </Container>
    </Box>
  );
};

// A squiggle, used the way the reference site uses one: to break a rhythm.
export const Squiggle = ({ color = rf.ink, width = 120, sx }) => (
  <Box component="svg" viewBox="0 0 120 16" width={width} height={16} sx={{ display: "block", ...sx }} aria-hidden="true">
    <path d="M2 8c6-8 12 8 18 0s12 8 18 0 12 8 18 0 12 8 18 0 12 8 18 0 12 8 18 0" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
  </Box>
);
